'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

type LoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

// Demo credentials
const demoCredentials = {
  email: 'priya.r@fluentblueprint.in',
  password: 'Blueprint@2026',
};

function DemoCredentialsBox({
  onAutofill,
}: {
  onAutofill: (email: string, password: string) => void;
}) {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(demoCredentials.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
    toast.success('Email copied!');
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(demoCredentials.password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
    toast.success('Password copied!');
  };

  return (
    <div className="mt-4 p-4 rounded-xl bg-info-bg border border-secondary/20">
      <p className="text-xs font-bold text-secondary uppercase tracking-wide mb-3">
        Demo Account — Click to Autofill
      </p>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium">Email</p>
            <p className="text-xs font-semibold text-foreground truncate font-mono">
              {demoCredentials.email}
            </p>
          </div>
          <button
            type="button"
            onClick={copyEmail}
            className="p-1.5 rounded-lg hover:bg-secondary/10 transition-colors"
            aria-label="Copy email"
          >
            {copiedEmail ? (
              <Check size={13} className="text-primary" />
            ) : (
              <Copy size={13} className="text-muted-foreground" />
            )}
          </button>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium">Password</p>
            <p className="text-xs font-semibold text-foreground font-mono">
              {demoCredentials.password}
            </p>
          </div>
          <button
            type="button"
            onClick={copyPassword}
            className="p-1.5 rounded-lg hover:bg-secondary/10 transition-colors"
            aria-label="Copy password"
          >
            {copiedPassword ? (
              <Check size={13} className="text-primary" />
            ) : (
              <Copy size={13} className="text-muted-foreground" />
            )}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onAutofill(demoCredentials.email, demoCredentials.password)}
        className="mt-3 w-full py-2 rounded-xl text-xs font-bold text-white btn-press"
        style={{ background: 'var(--secondary)' }}
      >
        Use Demo Account
      </button>
    </div>
  );
}

export default function LoginForm({ onSwitchToSignup }: { onSwitchToSignup: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const handleAutofill = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
    toast.success('Demo credentials filled in!');
  };

  // Backend integration point: replace with real auth API call
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));

    if (
      data.email === demoCredentials.email &&
      data.password === demoCredentials.password
    ) {
      toast.success('Welcome back, Priya! 🎉');
      setTimeout(() => router.push('/student-dashboard'), 800);
    } else {
      setError('root', {
        message:
          'Invalid credentials — use the demo account below to sign in',
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground">
          Sign in to continue your fluency journey
        </p>
      </div>

      {/* Google auth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border-2 border-border bg-card hover:bg-muted font-semibold text-sm text-foreground transition-all btn-press"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs font-medium text-muted-foreground">or sign in with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        {/* Root error */}
        {errors.root && (
          <div className="p-3 rounded-xl bg-danger/10 border border-danger/20">
            <p className="text-sm font-medium text-danger">{errors.root.message}</p>
          </div>
        )}

        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="login-email" className="text-sm font-semibold text-foreground">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="text-xs font-medium text-danger">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
              Password
            </label>
            <button
              type="button"
              className="text-xs font-semibold text-primary hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs font-medium text-danger">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="remember-me"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="remember-me" className="text-sm font-medium text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 rounded-2xl font-bold text-sm text-white btn-press disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary)', minWidth: 0 }}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={17} className="animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign In to My Dashboard'
          )}
        </button>
      </form>

      {/* Switch to signup */}
      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button
          onClick={onSwitchToSignup}
          className="font-bold text-primary hover:underline"
        >
          Create free account
        </button>
      </p>

      {/* Demo credentials */}
      <DemoCredentialsBox onAutofill={handleAutofill} />
    </div>
  );
}